import { useNavigate, useLocation, useParams } from 'react-router'
import { usePageTitle } from '$components'
import { FormStep, MultiStepFormProvider } from '$components'
import { CreatePetForm, CreatePetSchema } from '$features/pets/forms/create'
import { NavigableModal } from '$components'
import { FormSteps } from '$components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clientShowQueryKey, petStoreMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { detailsSchema, DetailsStep } from '$features/pets/forms/shared/details'
import { medicalInformationSchema, MedicalInformationStep } from '$features/pets/forms/medical-information'
import { notesSchema, NotesStep } from '$features/pets/forms/shared/notes'
import { DogBreed, Gender } from '$api-client/types.gen'
import { useFileUpload } from '$hooks/use-file-upload'

const steps: FormStep[] = [
  {
    id: 'details',
    label: 'Details',
    content: <DetailsStep />,
    schema: detailsSchema,
  },
  {
    id: 'medical-information',
    label: 'Medical Information',
    content: <MedicalInformationStep />,
    schema: medicalInformationSchema,
  },
  {
    id: 'notes',
    label: 'Notes',
    content: <NotesStep />,
    schema: notesSchema,
  },
]

export function CreateClientPetModal() {
  usePageTitle('Create New Pet')

  const navigate = useNavigate()
  const { state } = useLocation()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const fileHandler = useFileUpload()

  const handleClose = () => navigate(-1)

  const petMutation = useMutation({
    ...petStoreMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: clientShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })

      ToastHelper.createSuccess(ToastType.Pet)
      handleClose()
    },
    onError: () => ToastHelper.createError(ToastType.Pet),
  })

  const handleSubmit = (values: CreatePetSchema) => {
    petMutation.mutate({
      body: {
        // Details
        media_id: fileHandler.getFile('image')?.key,
        name: values.name,
        parent_id: values.parent.id,
        breed: values.breed.value as DogBreed,
        birth_date: values.date_of_birth,
        gender: values.gender as Gender,
        size_category_id: Number(values.size_category?.id),
        coat_type_id: Number(values.coat_type?.id),

        // Medical Information
        microchip_number: values.microchip_number,
        ...(values.weight && { weight: values.weight }),
        vaccinated: values.vaccinated,
        allergies: values.allergies,
        medical_notes: values.medical_notes,

        // Notes
        grooming_preferences: values.grooming_preferences,
        internal_notes: values.internal_notes,
      },
    })
  }

  const defaultValues = state?.client
    ? { parent: { id: state.client.id, value: `${state.client.first_name} ${state.client.last_name}` } }
    : undefined

  return (
    <MultiStepFormProvider steps={steps}>
      {({ getCurrentStep }) => (
        <CreatePetForm
          isLoading={petMutation.isPending}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          fileHandler={fileHandler}
          defaultValues={defaultValues}
        >
          <NavigableModal onClose={handleClose}>
            <NavigableModal.Header>
              <NavigableModal.Heading>
                Create Pet
              </NavigableModal.Heading>

              <FormSteps
                steps={steps}
                getCurrentStep={getCurrentStep}
              />
            </NavigableModal.Header>

            <NavigableModal.Content>
              <CreatePetForm.Content />
            </NavigableModal.Content>

            <NavigableModal.Footer>
              <CreatePetForm.Controls />
            </NavigableModal.Footer>
          </NavigableModal>
        </CreatePetForm>
      )}
    </MultiStepFormProvider>
  )
}
